import React, { useState, useEffect } from 'react';
import instance from '../api/Axios'; // Use your configured axios instance

const PermissionTemplateManager = () => {
    console.log('🔥 PermissionTemplateManager component rendering 🔥');
    console.log('🔥 Timestamp:', new Date().toISOString());
    const [templates, setTemplates] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permission_ids: []
    });
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertData, setAlertData] = useState({ type: '', title: '', message: '' });

    // Alert component
    const AlertModal = ({ show, type, title, message, onClose }) => {
        if (!show) return null;
        
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`alert alert-${type}`}>
                    <div className="alert-content">
                        <h4>{title}</h4>
                        <p>{message}</p>
                        <button onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        );
    };

    // Load templates and permissions on component mount
    useEffect(() => {
        console.log('🚀 PermissionTemplateManager useEffect running 🚀');
        console.log('🚀 About to call loadTemplates and loadGroupedPermissions');
        loadTemplates();
        loadGroupedPermissions();
    }, []);

    const loadTemplates = async () => {
        try {
            const response = await instance.get('/api/accounts/permission-templates/');
            setTemplates(response.data.results || response.data);
        } catch (error) {
            console.error('Error loading templates:', error);
        }
    };

    const loadGroupedPermissions = async () => {
        console.log('🎯 PermissionTemplateManager loadGroupedPermissions called 🎯');
        console.log('🎯 Timestamp:', new Date().toISOString());
        try {
            const response = await instance.get('/api/accounts/permissions/grouped/');
            console.log('✅ API response received ✅', response.status);
            console.log('✅ Response data structure:', Object.keys(response.data));
            
            // Extract all permissions from the grouped response
            const allPermissions = response.data.flatMap(group => group.permissions);
            
            console.log('📊 Total permissions from API:', allPermissions.length);
            console.log('📊 Sample permission structure:', allPermissions[0]);
            
            // Use the EXACT same list as BMOUserPermissionAssignment
            const implementedPermissions = [
                // User management
                'view_user', 'delete_user',
                // Funder management
                'view_funder', 'add_funder', 'change_funder', 'delete_funder',
                // Bursary management
                'view_bursary', 'add_bursary', 'change_bursary', 'delete_bursary',
                // Booking management
                'view_voucherbooking', 'add_voucherbooking', 'change_voucherbooking', 'delete_voucherbooking',
                // Funder officers
                'view_funderofficer', 'add_funderofficer', 'change_funderofficer', 'delete_funderofficer',
                // Funder representatives (primary and secondary - will be unified)
                'view_funderprimaryrepresentative', 'add_funderprimaryrepresentative', 'change_funderprimaryrepresentative', 'delete_funderprimaryrepresentative',
                'view_fundersecondaryrepresentative', 'add_fundersecondaryrepresentative', 'change_fundersecondaryrepresentative', 'delete_fundersecondaryrepresentative',
                // Funder applications
                'view_funderapplication', 'add_funderapplication', 'change_funderapplication', 'delete_funderapplication',
                // Funder agreements
                'view_funderagreement', 'add_funderagreement', 'change_funderagreement', 'delete_funderagreement',
                // Funder payments
                'view_funderpayment', 'add_funderpayment', 'change_funderpayment', 'delete_funderpayment',
                // Student management (basic)
                'view_student', 'add_student', 'change_student'
            ];
            
            // Filter to only these exact permissions
            const filteredPermissions = allPermissions.filter(permission => {
                const codename = permission.codename || permission.name?.toLowerCase();
                const isAllowed = implementedPermissions.includes(codename);
                if (isAllowed) {
                    console.log('✅ Including permission:', codename, permission.name);
                }
                return isAllowed;
            });
            
            console.log('🎯 Final filtered permissions count:', filteredPermissions.length);
            console.log('🎯 Final filtered permissions:', filteredPermissions.map(p => p.codename || p.name));
            
            // Group the filtered permissions by category
            const filteredGroupedPermissions = [{
                category: 'Available Permissions',
                permissions: filteredPermissions
            }];
            
            setGroupedPermissions(filteredGroupedPermissions);
            console.log('✅ setGroupedPermissions called with filtered data ✅');
        } catch (error) {
            console.error('❌ Error loading grouped permissions ❌', error);
        }
    };

    const validateGroupName = (name) => {
        // Check if name is empty
        if (!name || name.trim() === '') {
            return 'Group name is required.';
        }

        // Check if name is unique (only when creating new group)
        if (!selectedTemplate) {
            const existingNames = templates.map(template => template.name.toLowerCase());
            if (existingNames.includes(name.trim().toLowerCase())) {
                return 'A group with this name already exists. Please choose a different name.';
            }
        }

        return null;
    };

    const handleCreateTemplate = () => {
        console.log('🆕 New Group button clicked 🆕');
        console.log('🆕 Timestamp:', new Date().toISOString());
        setSelectedTemplate(null);
        setFormData({
            name: '',
            description: '',
            permission_ids: []
        });
        setIsEditing(false);
        console.log('🆕 Form reset for new group creation');
    };

    const handleEditTemplate = (template) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name,
            description: template.description,
            permission_ids: template.permissions.map(p => p.id)
        });
        setIsEditing(true);
    };

    // Helper function to get funder view permission ID
    const getFunderViewPermissionId = () => {
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const funderViewPermission = allPermissions.find(p => {
            const codename = p.codename || p.name?.toLowerCase();
            return codename === 'view_funder';
        });
        return funderViewPermission?.id;
    };

    // Helper function to check if user has any rep or officer permissions
    const hasAnyRepOrOfficerPermissions = () => {
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const repAndOfficerPermissions = allPermissions.filter(p => {
            const codename = p.codename || p.name?.toLowerCase();
            return codename.includes('representative') || codename.includes('funderofficer');
        });

        // Check if any rep/officer permissions are selected
        return repAndOfficerPermissions.some(p => formData.permission_ids.includes(p.id));
    };

    // Helper function to check if user has any Funder add/change/delete permissions
    const hasAnyFunderModifyPermissions = () => {
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const funderModifyPermissions = allPermissions.filter(p => {
            const codename = p.codename || p.name?.toLowerCase();
            return codename === 'add_funder' || codename === 'change_funder' || codename === 'delete_funder';
        });

        // Check if any funder modify permissions are selected
        return funderModifyPermissions.some(p => formData.permission_ids.includes(p.id));
    };

    // Helper function to get all add funder related permission IDs
    const getAddFunderRelatedPermissionIds = () => {
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const addFunderPermissions = allPermissions.filter(p => {
            const codename = p.codename || p.name?.toLowerCase();
            return codename === 'add_funder' || 
                   (codename.includes('add') && codename.includes('representative')) ||
                   codename === 'add_funderofficer';
        });
        return addFunderPermissions.map(p => p.id);
    };

    // Check if permission is part of the unified add funder group
    const isUnifiedAddPermission = (permissionId) => {
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const permission = allPermissions.find(p => p.id === permissionId);
        const codename = permission?.codename || permission?.name?.toLowerCase();
        return codename === 'add_funder' || 
               (codename.includes('add') && codename.includes('representative')) ||
               codename === 'add_funderofficer';
    };

    // Check if permission is part of the unified change group
    const isUnifiedChangePermission = (permissionId) => {
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const permission = allPermissions.find(p => p.id === permissionId);
        const codename = permission?.codename || permission?.name?.toLowerCase();
        return codename === 'change_funder' || 
               codename === 'change_funderprimaryrepresentative' ||
               codename === 'change_funderofficer';
    };

    // Helper function to get all change permission IDs (funder, rep, officer)
    const getChangeFunderRelatedPermissionIds = () => {
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const changePermissions = allPermissions.filter(p => {
            const codename = p.codename || p.name?.toLowerCase();
            return codename === 'change_funder' || 
                   codename === 'change_funderprimaryrepresentative' ||
                   codename === 'change_funderofficer';
        });
        return changePermissions.map(p => p.id);
    };

    // Enhanced permission toggle with funder view dependency
    const handlePermissionToggleWithDependency = (permissionId) => {
        // Check if this is a unified add permission
        if (isUnifiedAddPermission(permissionId)) {
            handleUnifiedAddPermissionToggle(permissionId);
            return;
        }

        // Check if this is a unified change permission
        if (isUnifiedChangePermission(permissionId)) {
            handleUnifiedChangePermissionToggle(permissionId);
            return;
        }

        // Continue with existing logic for other permissions
        handleSinglePermissionToggle(permissionId);
    };

    // Handle unified change permissions (funder, rep, officer)
    const handleUnifiedChangePermissionToggle = (permissionId) => {
        const currentlySelected = formData.permission_ids.includes(permissionId);
        const targetState = !currentlySelected;
        const unifiedPermissionIds = getChangeFunderRelatedPermissionIds();
        
        console.log('🔗 Unified change permission toggle:', {
            permissionId,
            currentlySelected,
            targetState,
            unifiedPermissionIds
        });

        let newPermissionIds = [...formData.permission_ids];

        if (targetState) {
            // Adding: add all unified change permissions
            unifiedPermissionIds.forEach(id => {
                if (!newPermissionIds.includes(id)) {
                    newPermissionIds.push(id);
                }
            });
            
            // Also add view_funder permission as a dependency for change permissions
            const viewFunderPermId = getFunderViewPermissionId();
            if (viewFunderPermId && !newPermissionIds.includes(viewFunderPermId)) {
                newPermissionIds.push(viewFunderPermId);
                console.log('✅ Auto-added view_funder due to change permissions');
            }
        } else {
            // Removing: remove all unified change permissions
            newPermissionIds = newPermissionIds.filter(id => !unifiedPermissionIds.includes(id));
        }

        setFormData(prev => ({
            ...prev,
            permission_ids: newPermissionIds
        }));
    };

    // Handle unified add permissions (funder, rep, officer)
    const handleUnifiedAddPermissionToggle = (permissionId) => {
        const currentlySelected = formData.permission_ids.includes(permissionId);
        const targetState = !currentlySelected;
        const unifiedPermissionIds = getAddFunderRelatedPermissionIds();
        
        console.log('🔄 Unified add permission toggle:', {
            permissionId,
            currentlySelected,
            targetState,
            unifiedPermissionIds
        });

        let newPermissionIds = [...formData.permission_ids];

        if (targetState) {
            // Adding: add all unified permissions
            unifiedPermissionIds.forEach(id => {
                if (!newPermissionIds.includes(id)) {
                    newPermissionIds.push(id);
                }
            });
            
            // Also add view_funder permission as a dependency
            const viewFunderPermId = getFunderViewPermissionId();
            if (viewFunderPermId && !newPermissionIds.includes(viewFunderPermId)) {
                newPermissionIds.push(viewFunderPermId);
            }
        } else {
            // Removing: remove all unified permissions
            newPermissionIds = newPermissionIds.filter(id => !unifiedPermissionIds.includes(id));
        }

        setFormData(prev => ({
            ...prev,
            permission_ids: newPermissionIds
        }));
    };

    // Handle individual permission toggle (non-unified)
    const handleSinglePermissionToggle = (permissionId) => {
        // Get permission details for dependency logic
        const allPermissions = groupedPermissions.flatMap(group => group.permissions);
        const permission = allPermissions.find(p => p.id === permissionId);
        const codename = permission?.codename || permission?.name?.toLowerCase();
        
        const currentlySelected = formData.permission_ids.includes(permissionId);
        const targetState = !currentlySelected;
        
        console.log('🔄 Permission toggle with dependency:', {
            permissionId,
            codename,
            currentlySelected,
            targetState
        });
        
        // Check if this is a funder view permission being removed
        if (codename === 'view_funder' && currentlySelected && !targetState) {
            if (hasAnyRepOrOfficerPermissions() || hasAnyFunderModifyPermissions()) {
                const conflictType = hasAnyRepOrOfficerPermissions() ? 'representative or officer' : 'add, change, or delete funder';
                setAlertData({
                    type: 'warning',
                    title: 'Cannot Remove Funder View Permission',
                    message: `You cannot remove the "View Funder" permission while ${conflictType} permissions are selected. Remove those permissions first.`
                });
                setShowAlert(true);
                return;
            }
        }

        // NEW: Check if this is add_funder being removed while rep/officer add permissions exist
        if (codename === 'add_funder' && currentlySelected && !targetState) {
            const allPermissions = groupedPermissions.flatMap(group => group.permissions);
            const hasAddRepOrOfficerPermissions = allPermissions.some(p => {
                const pCodename = p.codename || p.name?.toLowerCase();
                return (pCodename === 'add_funderprimaryrepresentative' || 
                        pCodename === 'add_fundersecondaryrepresentative' || 
                        pCodename === 'add_funderofficer') && 
                       formData.permission_ids.includes(p.id);
            });
            
            if (hasAddRepOrOfficerPermissions) {
                setAlertData({
                    type: 'warning',
                    title: 'Cannot Remove Add Funder Permission',
                    message: 'You cannot remove the "Add Funder" permission while add representative or add officer permissions are selected. Remove those permissions first.'
                });
                setShowAlert(true);
                return;
            }
        }

        // NEW: Check if this is rep/officer add permission being removed while add_funder exists
        if (currentlySelected && !targetState && (
            codename === 'add_funderprimaryrepresentative' || 
            codename === 'add_fundersecondaryrepresentative' || 
            codename === 'add_funderofficer'
        )) {
            const allPermissions = groupedPermissions.flatMap(group => group.permissions);
            const hasAddFunderPermission = allPermissions.some(p => {
                const pCodename = p.codename || p.name?.toLowerCase();
                return pCodename === 'add_funder' && formData.permission_ids.includes(p.id);
            });
            
            if (hasAddFunderPermission) {
                const permissionType = codename.includes('representative') ? 'representative' : 'officer';
                setAlertData({
                    type: 'warning',
                    title: `Cannot Remove Add ${permissionType.charAt(0).toUpperCase() + permissionType.slice(1)} Permission`,
                    message: `You cannot remove the "Add ${permissionType.charAt(0).toUpperCase() + permissionType.slice(1)}" permission while the "Add Funder" permission is selected. Remove the "Add Funder" permission first.`
                });
                setShowAlert(true);
                return;
            }
        }

        let newPermissionIds = [...formData.permission_ids];
        
        // Toggle the permission
        if (targetState) {
            newPermissionIds.push(permissionId);
        } else {
            newPermissionIds = newPermissionIds.filter(id => id !== permissionId);
        }

        // Check if this is a rep/officer permission and we need funder view dependency
        if (targetState && (codename?.includes('representative') || codename?.includes('funderofficer'))) {
            const funderViewId = getFunderViewPermissionId();
            if (funderViewId && !newPermissionIds.includes(funderViewId)) {
                newPermissionIds.push(funderViewId);
                console.log('✅ Auto-added view_funder due to rep/officer permission');
            }
        }

        // Check if this is a Funder add/change/delete permission and we need funder view dependency
        if (targetState && (codename === 'add_funder' || codename === 'change_funder' || codename === 'delete_funder')) {
            const funderViewId = getFunderViewPermissionId();
            if (funderViewId && !newPermissionIds.includes(funderViewId)) {
                newPermissionIds.push(funderViewId);
                console.log('✅ Auto-added view_funder due to funder modify permission');
            }
        }

        // NEW: If adding 'add_funder', also add representative and officer add permissions
        if (targetState && codename === 'add_funder') {
            const allPermissions = groupedPermissions.flatMap(group => group.permissions);
            
            // Find add representative and officer permissions
            const addPrimaryRepId = allPermissions.find(p => 
                (p.codename || p.name?.toLowerCase()) === 'add_funderprimaryrepresentative'
            )?.id;
            const addSecondaryRepId = allPermissions.find(p => 
                (p.codename || p.name?.toLowerCase()) === 'add_fundersecondaryrepresentative'
            )?.id;
            const addOfficerId = allPermissions.find(p => 
                (p.codename || p.name?.toLowerCase()) === 'add_funderofficer'
            )?.id;
            
            // Auto-add representative and officer add permissions
            [addPrimaryRepId, addSecondaryRepId, addOfficerId].forEach(permId => {
                if (permId && !newPermissionIds.includes(permId)) {
                    newPermissionIds.push(permId);
                    console.log('✅ Auto-added add rep/officer permission due to add_funder');
                }
            });
        }
        
        setFormData(prev => ({
            ...prev,
            permission_ids: newPermissionIds
        }));
    };

    // Unified handler for representative permissions in CRUD table
    const handleUnifiedRepresentativeToggle = (primaryPermId, secondaryPermId, currentState) => {
        const targetState = !currentState; // If currently checked, uncheck (remove), if unchecked, check (add)
        
        console.log('🔄 Unified representative toggle:', {
            primaryPermId,
            secondaryPermId,
            currentState,
            targetState
        });
        
        let newPermissionIds = [...formData.permission_ids];
        
        // Handle both permissions with the same target state
        [primaryPermId, secondaryPermId].forEach(permId => {
            if (!permId) return; // Skip if permission doesn't exist
            
            if (targetState && !newPermissionIds.includes(permId)) {
                // Want to add permission and don't currently have it
                newPermissionIds.push(permId);
            } else if (!targetState && newPermissionIds.includes(permId)) {
                // Want to remove permission and currently have it
                newPermissionIds = newPermissionIds.filter(id => id !== permId);
            }
        });

        // Check if we need to add funder view dependency when adding rep permissions
        if (targetState) {
            const funderViewId = getFunderViewPermissionId();
            if (funderViewId && !newPermissionIds.includes(funderViewId)) {
                newPermissionIds.push(funderViewId);
                console.log('✅ Auto-added view_funder due to representative permission');
            }

            // NEW: Check if we need to add add_funder dependency when adding rep add permissions
            const allPermissions = groupedPermissions.flatMap(group => group.permissions);
            const isAddingRepAddPermissions = [primaryPermId, secondaryPermId].some(permId => {
                if (!permId) return false;
                const permission = allPermissions.find(p => p.id === permId);
                const pCodename = permission?.codename || permission?.name?.toLowerCase();
                return pCodename === 'add_funderprimaryrepresentative' || pCodename === 'add_fundersecondaryrepresentative';
            });

            if (isAddingRepAddPermissions) {
                const addFunderId = allPermissions.find(p => 
                    (p.codename || p.name?.toLowerCase()) === 'add_funder'
                )?.id;
                
                if (addFunderId && !newPermissionIds.includes(addFunderId)) {
                    newPermissionIds.push(addFunderId);
                    console.log('✅ Auto-added add_funder due to add representative permission');
                }
            }
        }
        
        setFormData(prev => ({
            ...prev,
            permission_ids: newPermissionIds
        }));
    };

    const handleSaveTemplate = async () => {
        // Validate group name
        const nameError = validateGroupName(formData.name);
        if (nameError) {
            setAlertData({
                type: 'error',
                title: 'Error',
                message: nameError
            });
            setShowAlert(true);
            return;
        }

        setLoading(true);
        try {
            const url = selectedTemplate 
                ? `/api/accounts/permission-templates/${selectedTemplate.id}/`
                : '/api/accounts/permission-templates/';
            
            const method = selectedTemplate ? 'put' : 'post';
            
            const response = await instance[method](url, formData);
            
            // Show success alert
            setAlertData({
                type: 'success',
                title: 'Success!',
                message: selectedTemplate 
                    ? 'Group updated successfully.' 
                    : 'Group created successfully.'
            });
            setShowAlert(true);
            
            // Reload templates list
            await loadTemplates();
            
            // If editing existing template, keep it selected and reload its data
            // If creating new template, keep the form in create mode but reset it
            if (selectedTemplate) {
                // Keep the template selected for continued editing
                // The form data is already up to date from the save
                setIsEditing(true);
            } else {
                // For new template creation, get the created template and select it
                if (response.data && response.data.id) {
                    const newTemplate = response.data;
                    setSelectedTemplate(newTemplate);
                    setFormData({
                        name: newTemplate.name,
                        description: newTemplate.description || '',
                        permission_ids: newTemplate.permissions.map(p => p.id)
                    });
                    setIsEditing(true);
                } else {
                    // Fallback: reset form
                    handleCreateTemplate();
                }
            }
            
        } catch (error) {
            console.error('Error saving template:', error);
            setAlertData({
                type: 'error',
                title: 'Error',
                message: 'Failed to save group. Please try again.'
            });
            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;
        
        try {
            await instance.delete(`/api/accounts/permission-templates/${templateId}/`);
            loadTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
        }
    };

    const createInitialTemplates = async () => {
        setLoading(true);
        try {
            // Get the predefined template structure
            const structureResponse = await instance.get('/api/accounts/permissions/template_structure/');
            
            // Create templates from structure
            await instance.post('/api/accounts/permission-templates/create_from_structure/', structureResponse.data);
            
            // Show success alert
            setAlertData({
                type: 'success',
                title: 'Success!',
                message: 'Default groups copied successfully.'
            });
            setShowAlert(true);
            
            loadTemplates();
        } catch (error) {
            console.error('Error creating initial templates:', error);
            setAlertData({
                type: 'error',
                title: 'Error',
                message: 'Failed to copy default groups. Please try again.'
            });
            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="permission-template-manager">
            <AlertModal 
                show={showAlert}
                type={alertData.type}
                title={alertData.title}
                message={alertData.message}
                onClose={() => setShowAlert(false)}
            />
            
            <div className="header">
                <h2>Permission Group Management</h2>
                <div className="actions">
                    <button onClick={createInitialTemplates} disabled={loading}>
                        Copy Default Groups
                    </button>
                    <button onClick={handleCreateTemplate}>
                        New Group
                    </button>
                </div>
            </div>

            <div className="content">
                {/* Template List */}
                <div className="template-list">
                    <h3>Existing Groups</h3>
                    {templates.length === 0 ? (
                        <p>No groups created yet. Click "Copy Default Groups" to get started.</p>
                    ) : (
                        <div className="template-cards">
                            {templates.map(template => (
                                <div key={template.id} className="template-card">
                                    <h4>{template.name}</h4>
                                    <p>{template.description}</p>
                                    <div className="template-actions">
                                        <button onClick={() => handleEditTemplate(template)}>
                                            Edit
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteTemplate(template.id)}
                                            className="danger"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Template Form */}
                {(selectedTemplate || !isEditing) && (
                    <div className="template-form">
                        <h3>{selectedTemplate ? 'Edit Group' : 'Create New Group'}</h3>
                        
                        <div className="form-group">
                            <label>Group Name:</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Enter group name"
                            />
                        </div>

                        <div className="form-group">
                            <label>Description:</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                placeholder="Enter group description"
                                rows="3"
                            />
                        </div>

                        <div className="permissions-section">
                            <h4>Permissions</h4>
                            
                            {/* Unified Funder Management CRUD Table */}
                            {(() => {
                                // Get all funder-related permissions (funders, representatives, officers)
                                const allFunderPermissions = groupedPermissions.flatMap(group => 
                                    group.permissions.filter(p => {
                                        const codename = p.codename || p.name?.toLowerCase();
                                        return codename.includes('funder') && !codename.includes('funderapplication') &&
                                               !codename.includes('funderagreement') && !codename.includes('funderpayment');
                                    })
                                );
                                
                                // Find the main funder permissions
                                const funderPerms = allFunderPermissions.filter(p => {
                                    const codename = p.codename || p.name?.toLowerCase();
                                    return codename.includes('funder') && !codename.includes('funderofficer') && 
                                           !codename.includes('funderrepresentative') && !codename.includes('funderprimary') && 
                                           !codename.includes('fundersecondary');
                                });
                                
                                return allFunderPermissions.length > 0 ? (
                                    <div className="unified-permission-section">
                                        <h6 className="unified-section-title">
                                            <i className="fas fa-building me-2"></i>
                                            Funder Management (Unified)
                                        </h6>
                                        
                                        <div className="crud-table">
                                            <div className="crud-table-header">
                                                <div className="crud-header-cell">Permission Type</div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-eye me-1"></i>
                                                    View
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-plus me-1"></i>
                                                    Add
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-edit me-1"></i>
                                                    Change
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-trash me-1"></i>
                                                    Delete
                                                </div>
                                            </div>
                                            
                                            <div className="crud-table-row">
                                                <div className="crud-row-label">
                                                    <i className="fas fa-building me-2"></i>
                                                    <strong>Funders, Representatives & Officers</strong>
                                                    <small>All funder-related permissions unified</small>
                                                </div>
                                                
                                                {['view', 'add', 'change', 'delete'].map(action => {
                                                    // Find all permissions for this action across all funder types
                                                    const funderPerm = funderPerms.find(p => 
                                                        (p.codename || p.name?.toLowerCase()) === `${action}_funder`
                                                    );
                                                    const repPerm = allFunderPermissions.find(p => 
                                                        (p.codename || p.name?.toLowerCase()) === `${action}_funderprimaryrepresentative`
                                                    );
                                                    const officerPerm = allFunderPermissions.find(p => 
                                                        (p.codename || p.name?.toLowerCase()) === `${action}_funderofficer`
                                                    );
                                                    
                                                    // Check if any of these permissions are selected
                                                    const isFunderChecked = funderPerm ? formData.permission_ids.includes(funderPerm.id) : false;
                                                    const isRepChecked = repPerm ? formData.permission_ids.includes(repPerm.id) : false;
                                                    const isOfficerChecked = officerPerm ? formData.permission_ids.includes(officerPerm.id) : false;
                                                    const isAnyChecked = isFunderChecked || isRepChecked || isOfficerChecked;
                                                    
                                                    return (
                                                        <div key={action} className="crud-checkbox-cell">
                                                            <label className="crud-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isAnyChecked}
                                                                    onChange={() => {
                                                                        // Unified toggle for this action across all funder types
                                                                        const targetState = !isAnyChecked;
                                                                        
                                                                        // Toggle all related permissions for this action to the same state
                                                                        [funderPerm, repPerm, officerPerm].forEach(perm => {
                                                                            if (perm) {
                                                                                const isCurrentlyChecked = formData.permission_ids.includes(perm.id);
                                                                                if (targetState !== isCurrentlyChecked) {
                                                                                    handlePermissionToggleWithDependency(perm.id);
                                                                                }
                                                                            }
                                                                        });
                                                                    }}
                                                                    disabled={!funderPerm && !repPerm && !officerPerm}
                                                                />
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        
                                        <div className="crud-info">
                                            <small className="text-muted">
                                                <i className="fas fa-info-circle me-1"></i>
                                                Each action checkbox controls the same permission for Funders, Representatives, and Officers together
                                            </small>
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                            
                            {/* Bursary CRUD Table */}
                            {(() => {
                                // Get all bursary permissions from grouped permissions
                                const allBursaryPermissions = groupedPermissions.flatMap(group => 
                                    group.permissions.filter(p => {
                                        const codename = p.codename || p.name?.toLowerCase();
                                        return codename === 'view_bursary' || codename === 'add_bursary' || 
                                               codename === 'change_bursary' || codename === 'delete_bursary';
                                    })
                                );
                                
                                return allBursaryPermissions.length > 0 ? (
                                    <div className="unified-permission-section">
                                        <h6 className="unified-section-title">
                                            <i className="fas fa-award me-2"></i>
                                            Bursary Management
                                        </h6>
                                        
                                        <div className="crud-table">
                                            <div className="crud-table-header">
                                                <div className="crud-header-cell">Permission Type</div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-eye me-1"></i>
                                                    View
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-plus me-1"></i>
                                                    Add
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-edit me-1"></i>
                                                    Change
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-trash me-1"></i>
                                                    Delete
                                                </div>
                                            </div>
                                            
                                            <div className="crud-table-row">
                                                <div className="crud-row-label">
                                                    <i className="fas fa-award me-2"></i>
                                                    <strong>Bursary Programs</strong>
                                                    <small>Scholarship & Financial Aid Management</small>
                                                </div>
                                                
                                                {['view', 'add', 'change', 'delete'].map(action => {
                                                    // Find the permission for this action
                                                    const permission = allBursaryPermissions.find(p => 
                                                        (p.codename || p.name?.toLowerCase()) === `${action}_bursary`
                                                    );
                                                    
                                                    const isChecked = permission ? formData.permission_ids.includes(permission.id) : false;
                                                    
                                                    return (
                                                        <div key={action} className="crud-checkbox-cell">
                                                            <label className="crud-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => {
                                                                        if (permission) {
                                                                            handlePermissionToggleWithDependency(permission.id);
                                                                        }
                                                                    }}
                                                                    disabled={!permission}
                                                                />
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        
                                        <div className="crud-info">
                                            <small className="text-muted">
                                                <i className="fas fa-info-circle me-1"></i>
                                                Manage permissions for bursary programs and financial aid administration
                                            </small>
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                            
                            {/* Booking Management CRUD Table */}
                            {(() => {
                                // Get all booking permissions
                                const allBookingPermissions = groupedPermissions.flatMap(group => 
                                    group.permissions.filter(p => {
                                        const codename = p.codename || p.name?.toLowerCase();
                                        return codename === 'view_voucherbooking' || codename === 'add_voucherbooking' || 
                                               codename === 'change_voucherbooking' || codename === 'delete_voucherbooking';
                                    })
                                );
                                
                                return allBookingPermissions.length > 0 ? (
                                    <div className="unified-permission-section">
                                        <h6 className="unified-section-title">
                                            <i className="fas fa-calendar-check me-2"></i>
                                            Booking Management
                                        </h6>
                                        
                                        <div className="crud-table">
                                            <div className="crud-table-header">
                                                <div className="crud-header-cell">Permission Type</div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-eye me-1"></i>
                                                    View
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-plus me-1"></i>
                                                    Add
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-edit me-1"></i>
                                                    Change
                                                </div>
                                                <div className="crud-header-cell">
                                                    <i className="fas fa-trash me-1"></i>
                                                    Delete
                                                </div>
                                            </div>
                                            
                                            <div className="crud-table-row">
                                                <div className="crud-row-label">
                                                    <i className="fas fa-bed me-2"></i>
                                                    <strong>Accommodation Bookings</strong>
                                                    <small>Student Accommodation Booking Management</small>
                                                </div>
                                                
                                                {['view', 'add', 'change', 'delete'].map(action => {
                                                    // Find the permission for this action
                                                    const permission = allBookingPermissions.find(p => 
                                                        (p.codename || p.name?.toLowerCase()) === `${action}_voucherbooking`
                                                    );
                                                    
                                                    const isChecked = permission ? formData.permission_ids.includes(permission.id) : false;
                                                    
                                                    // Special behavior for add action
                                                    let isDisabled = !permission;
                                                    let title = '';
                                                    let className = '';
                                                    
                                                    if (action === 'add') {
                                                        // Add is always checked and disabled - students can book openly
                                                        title = 'Students can create bookings without special permissions';
                                                        className = 'booking-add-disabled';
                                                    } else if (action === 'view') {
                                                        title = 'Controls if Bookings appears in sidebar navigation';
                                                    } else if (action === 'change') {
                                                        title = 'Allows accepting/rejecting bookings (BMO officers)';
                                                    } else if (action === 'delete') {
                                                        title = 'Controls booking cancellation permissions';
                                                    }
                                                    
                                                    return (
                                                        <div key={action} className="crud-checkbox-cell">
                                                            <label className={`crud-checkbox ${className}`} title={title}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={action === 'add' ? true : isChecked}
                                                                    onChange={() => {
                                                                        if (permission && action !== 'add') {
                                                                            handlePermissionToggleWithDependency(permission.id);
                                                                        }
                                                                    }}
                                                                    disabled={action === 'add' ? true : isDisabled}
                                                                />
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        
                                        <div className="crud-info">
                                            <small className="text-muted">
                                                <i className="fas fa-info-circle me-1"></i>
                                                <strong>View:</strong> Controls sidebar visibility | 
                                                <strong>Add:</strong> Open to students | 
                                                <strong>Change:</strong> Accept/reject bookings | 
                                                <strong>Delete:</strong> Booking management
                                            </small>
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                            
                            {/* Other Permissions (Students, Users, Applications, Agreements, Payments) */}
                            {(() => {
                                // Get permissions that aren't in CRUD tables
                                const otherPermissions = groupedPermissions.flatMap(group => 
                                    group.permissions.filter(p => {
                                        const codename = p.codename || p.name?.toLowerCase();
                                        // Exclude permissions that have their own CRUD tables
                                        return !codename.includes('representative') && 
                                               !codename.includes('funderofficer') &&
                                               !codename.includes('bursary') &&
                                               // Booking management (handled in CRUD table)
                                               !codename.includes('voucherbooking') &&
                                               !(codename.includes('funder') && !codename.includes('funderapplication') && 
                                                 !codename.includes('funderagreement') && !codename.includes('funderpayment'));
                                    })
                                );
                                
                                return otherPermissions.length > 0 ? (
                                    <div className="unified-permission-section">
                                        <h6 className="unified-section-title">
                                            <i className="fas fa-cog me-2"></i>
                                            Other Permissions
                                        </h6>
                                        
                                        <div className="permission-checkboxes">
                                            {otherPermissions.map(permission => (
                                                <label key={permission.id} className="permission-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.permission_ids.includes(permission.id)}
                                                        onChange={() => handlePermissionToggleWithDependency(permission.id)}
                                                    />
                                                    <span>{permission.name}</span>
                                                    <code>({permission.codename})</code>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                        </div>

                        <div className="form-actions">
                            <button onClick={handleSaveTemplate} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Group'}
                            </button>
                            <button onClick={handleCreateTemplate}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .permission-template-manager {
                    padding: 20px;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                }

                .actions {
                    display: flex;
                    gap: 10px;
                }

                .content {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 30px;
                }

                .template-cards {
                    display: grid;
                    gap: 15px;
                }

                .template-card {
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    padding: 15px;
                    background: white;
                }

                .template-card h4 {
                    margin: 0 0 10px 0;
                    color: #333;
                }

                .template-stats {
                    color: #666;
                    font-size: 14px;
                    margin: 10px 0;
                }

                .template-actions {
                    display: flex;
                    gap: 10px;
                    margin-top: 15px;
                }

                .template-form {
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    padding: 20px;
                    background: white;
                    max-height: 800px;
                    overflow-y: auto;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 5px;
                    font-weight: bold;
                }

                .form-group input,
                .form-group textarea {
                    width: 100%;
                    padding: 8px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                }

                .permission-groups {
                    max-height: 400px;
                    overflow-y: auto;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    padding: 15px;
                }

                .permission-group {
                    margin-bottom: 20px;
                    padding-bottom: 15px;
                    border-bottom: 1px solid #eee;
                }

                .permission-group h5 {
                    margin: 0 0 10px 0;
                    color: #4472C4;
                    font-size: 16px;
                }

                .permission-checkboxes {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 8px;
                }

                .permission-checkbox {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 5px;
                    border-radius: 4px;
                    cursor: pointer;
                }

                .permission-checkbox:hover {
                    background: #f5f5f5;
                }

                .permission-checkbox input {
                    width: auto;
                }

                .permission-checkbox code {
                    font-size: 12px;
                    color: #666;
                    background: #f0f0f0;
                    padding: 2px 4px;
                    border-radius: 3px;
                }

                .form-actions {
                    display: flex;
                    gap: 10px;
                    margin-top: 20px;
                    padding-top: 15px;
                    border-top: 1px solid #eee;
                }

                button {
                    padding: 10px 20px;
                    border: none;
                    border-radius: 4px;
                    background: #4472C4;
                    color: white;
                    cursor: pointer;
                    font-size: 14px;
                }

                button:hover {
                    background: #365a96;
                }

                button:disabled {
                    background: #ccc;
                    cursor: not-allowed;
                }

                button.danger {
                    background: #dc3545;
                }

                button.danger:hover {
                    background: #c82333;
                }

                .alert {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: 1000;
                    min-width: 300px;
                    max-width: 500px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    background: white;
                    border: 1px solid #dee2e6;
                }

                .alert-success {
                    border-color: #d1e7dd;
                    background: #f8f9fa;
                }

                .alert-error {
                    border-color: #f5c6cb;
                    background: #f8f9fa;
                }

                .alert-content {
                    padding: 20px;
                    text-align: center;
                }

                .alert-content h4 {
                    margin: 0 0 10px 0;
                    font-size: 18px;
                    font-weight: 600;
                }

                .alert-success .alert-content h4 {
                    color: #198754;
                }

                .alert-error .alert-content h4 {
                    color: #dc3545;
                }

                .alert-content p {
                    margin: 0 0 20px 0;
                    color: #666;
                    font-size: 14px;
                }

                .alert-content button {
                    padding: 8px 20px;
                    border: none;
                    border-radius: 4px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                }

                .alert-success .alert-content button {
                    background: #198754;
                    color: white;
                }

                .alert-success .alert-content button:hover {
                    background: #157347;
                }

                .alert-error .alert-content button {
                    background: #dc3545;
                    color: white;
                }

                .alert-error .alert-content button:hover {
                    background: #c82333;
                }

                /* CRUD Table Styles */
                .unified-permission-section {
                    margin-bottom: 25px;
                    border: 1px solid #e1e5e9;
                    border-radius: 8px;
                    background: white;
                    overflow: hidden;
                }

                .unified-section-title {
                    margin: 0;
                    padding: 15px 20px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #e1e5e9;
                    color: #2c3e50;
                    font-size: 1rem;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                }

                .crud-table {
                    width: 100%;
                    border-collapse: collapse;
                    display: table;
                }

                .crud-table-header {
                    display: table-row;
                    background: #f8f9fa;
                    border-bottom: 2px solid #e1e5e9;
                }

                .crud-header-cell {
                    display: table-cell;
                    padding: 12px 15px;
                    font-weight: 600;
                    color: #495057;
                    border-right: 1px solid #e1e5e9;
                    text-align: center;
                    vertical-align: middle;
                    font-size: 0.85rem;
                }

                .crud-header-cell:first-child {
                    text-align: left;
                    width: 40%;
                }

                .crud-header-cell:last-child {
                    border-right: none;
                }

                .crud-table-row {
                    display: table-row;
                    border-bottom: 1px solid #f1f3f4;
                }

                .crud-table-row:hover {
                    background: #f8f9fa;
                }

                .crud-row-label {
                    display: table-cell;
                    padding: 15px 20px;
                    vertical-align: middle;
                    border-right: 1px solid #e1e5e9;
                }

                .crud-row-label strong {
                    display: block;
                    color: #2c3e50;
                    font-size: 0.9rem;
                    margin-bottom: 2px;
                }

                .crud-row-label small {
                    color: #6c757d;
                    font-size: 0.75rem;
                    display: block;
                }

                .crud-checkbox-cell {
                    display: table-cell;
                    padding: 15px;
                    text-align: center;
                    vertical-align: middle;
                    border-right: 1px solid #e1e5e9;
                }

                .crud-checkbox-cell:last-child {
                    border-right: none;
                }

                .crud-checkbox {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0;
                    cursor: pointer;
                }

                .crud-checkbox input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                    margin: 0;
                    cursor: pointer;
                    accent-color: #007bff;
                }

                .crud-info {
                    padding: 12px 20px;
                    background: #f8f9fa;
                    border-top: 1px solid #e1e5e9;
                }

                .crud-info .text-muted {
                    color: #6c757d;
                    margin: 0;
                    font-size: 0.8rem;
                }

                @media (max-width: 768px) {
                    .content {
                        grid-template-columns: 1fr;
                    }
                    
                    .permission-checkboxes {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default PermissionTemplateManager;
